import moment, { Moment } from 'moment-timezone';
import { DATE_FORMAT, START_DAY_WORK, TIMEZONE } from '../../constants';

export type SearchCardArea = {
  id: number;
  name: string;
};

export type ResolveSearchCardAreaBadgeInput = {
  affiliationAreaName?: string;
  shiftAreaId?: number | null;
  areas: SearchCardArea[];
  /**
   * When true, show affiliation area instead of shift area.
   * Only set when area filter is on and Ready-to-Go uses p.areaId
   * (see shouldUseAffiliationAreaForSearchBadge).
   */
  useAffiliationArea?: boolean;
};

export type ShouldUseAffiliationAreaInput = {
  isReadyToGo?: boolean;
  selectedDate?: string;
  shiftStartTime?: string;
  /**
   * Only switch to affiliation when area filter is active
   * (mirrors backend `if (!!activityAreas.length)` branch).
   */
  hasAreaFilter?: boolean;
  /** Injectable for tests. Defaults to now in app timezone. */
  now?: Moment;
};

const START_HOUR_OF_THE_DAY = Number(START_DAY_WORK.split(':')[0]);

/**
 * Mirrors teco-backend female search area filter for Ready-to-Go:
 * only when activityAreas are filtered, and before shift start
 * (incl. overnight < 11:00 → +24h), use affiliation area.
 * Without area filter the badge stays on shift area (Issue #2534).
 */
export function shouldUseAffiliationAreaForSearchBadge({
  isReadyToGo,
  selectedDate,
  shiftStartTime,
  hasAreaFilter = false,
  now = moment().tz(TIMEZONE),
}: ShouldUseAffiliationAreaInput): boolean {
  if (!hasAreaFilter) return false;
  if (!isReadyToGo || !selectedDate) return false;

  const selectedDay = moment(selectedDate).tz(TIMEZONE).format(DATE_FORMAT);
  if (selectedDay !== now.format(DATE_FORMAT)) return false;

  if (!shiftStartTime) return true;

  const startHour = Number(shiftStartTime.split(':')[0]);
  let shiftStart = moment.tz(
    `${now.format('YYYY-MM-DD')} ${shiftStartTime}`,
    'YYYY-MM-DD HH:mm',
    TIMEZONE,
  );
  if (startHour < START_HOUR_OF_THE_DAY) {
    shiftStart = shiftStart.add(1, 'day');
  }

  return now.isBefore(shiftStart);
}

/**
 * Search cards always show a location badge (unlike schedule cells which can
 * use "-"). Day-off / to-be-confirmed without a shift falls back to affiliation.
 * With area filter + Ready-to-Go before shift start, use affiliation so the
 * badge matches the filter; otherwise keep shift area.
 */
export function resolveSearchCardAreaBadge({
  affiliationAreaName,
  shiftAreaId,
  areas,
  useAffiliationArea = false,
}: ResolveSearchCardAreaBadgeInput): string {
  if (useAffiliationArea) {
    return affiliationAreaName ?? '';
  }

  if (shiftAreaId != null) {
    const shiftAreaName = areas.find((area) => area.id === shiftAreaId)?.name;
    // Never fall back to affiliation when a shift area is specified but the
    // master is missing/unresolved — that would show the wrong meetup area.
    return shiftAreaName ?? '';
  }

  return affiliationAreaName ?? '';
}
